import React, { useEffect, useRef, useState } from 'react';

// Constants
import { BUTTON_TYPES, JobModes, LABEL_TYPES, TINY_MCE_PLUGINS } from '../../constants/common';
import {
  getFormErrorMessage,
  isDarkMode,
  isFormFieldValid,
  openResumeLink
} from '../../utils/common';

// Components
import { Dialog } from 'primereact/dialog';
import classNames from 'classnames';
import Label from '../../components/Label';
import CustomButton from '../../components/CustomButton';
import FormikTextField from '../../components/form/FormikTextField';
import FormikInputNumber from '../../components/form/FormikInputNumber';
import FormikDropdown from '../../components/form/FormikDropdown';
import FormikAutocomplete from '../../components/form/FormikAutocomplete';
import FormikMultiSelect from '../../components/form/FormikMultiSelect';

// Utility Functions
import { onSuggestSkils } from '../../utils/pageUtils';
import { addJob, editJob, getJobById } from '../../redux/actions/jobs';
import { getInterviewTechnology } from '../../redux/actions/hr';
import { updateData } from '../../redux/slices/jobs';
import { apiStatusClear } from '../../redux/slices/apiStatus';
import { ResumeButton } from '../../components/ActionButtons';

// Utility Packages
import { useFormik } from 'formik';
import { Editor } from '@tinymce/tinymce-react';
import { useDispatch, useSelector } from 'react-redux';
import { addEditJobSchema } from '../../schema/job.schema';

const imagePathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/job`;

export default function AddEditJob(props) {
  const { onHide, ID, show } = props;
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const imageRef = useRef(null);

  const { isSucceed } = useSelector((state) => state.apiStatus);
  const { jobDetails } = useSelector((state) => state.jobs);
  const interviewTechnologyType = useSelector((state) => state.hrs.interviewTechnologyType);

  const [skilSuggestion, setSkilSuggestion] = useState([]);
  const [isUpdatedSuccess, setIsUpdatedSuccess] = useState(false);
  const [isImage, setIsImage] = useState('');
  const [imageName, setImageName] = useState('');
  const [jobData, setJobData] = useState({
    jobTitle: '',
    position: '',
    technologies: [],
    skills: [],
    jobDescription: '',
    jobImage: '',
    jobMode: '',
    experience: null,
    maxExperience: null
  });

  const handleReset = () => {
    setJobData({
      jobTitle: '',
      position: '',
      technologies: [],
      skills: [],
      jobDescription: '',
      jobImage: '',
      jobMode: '',
      experience: null,
      maxExperience: null
    });
    setImageName('');
    setIsImage('');
    formik.resetForm();
  };

  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  useEffect(() => {
    if (ID && jobDetails && Object.keys(jobDetails).length !== 0) {
      let techValues = [];
      if (Array.isArray(jobDetails?.technologies)) {
        techValues = jobDetails.technologies
          .map((t) => (typeof t === 'object' && t !== null ? t.ID || t.id : t))
          .filter(Boolean);
      } else if (Array.isArray(jobDetails?.Technologies)) {
        techValues = jobDetails.Technologies.map((t) =>
          typeof t === 'object' && t !== null ? t.ID || t.id : t
        ).filter(Boolean);
      } else if (typeof jobDetails?.technologies === 'string' && jobDetails.technologies) {
        techValues = jobDetails.technologies
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      } else if (typeof jobDetails?.Technologies === 'string' && jobDetails.Technologies) {
        techValues = jobDetails.Technologies.split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }

      setJobData({
        jobTitle: `${jobDetails?.JobTitle || ''}`,
        position: `${jobDetails?.Position || ''}`,
        technologies: techValues,
        skills: jobDetails?.Skills || [],
        jobDescription: `${jobDetails?.JobDescription || ''}`,
        jobImage: `${jobDetails?.JobImage || ''}`,
        jobMode: `${jobDetails?.JobMode || 'WFO'}`,
        experience:
          jobDetails?.Experience !== undefined && jobDetails?.Experience !== null
            ? Number(jobDetails.Experience)
            : null,
        maxExperience:
          jobDetails?.MaxExperience !== undefined && jobDetails?.MaxExperience !== null
            ? Number(jobDetails.MaxExperience)
            : null
      });
      if (jobDetails?.JobImage) {
        setIsImage(jobDetails?.JobImage);
      } else {
        setIsImage('');
      }
    }
  }, [jobDetails, ID]);

  useEffect(() => {
    ID && dispatch(getJobById(ID));
  }, [dispatch, ID]);

  useEffect(() => {
    if (show && isUpdatedSuccess && isSucceed) {
      onHide();
      handleReset();
      dispatch(updateData());
      dispatch(apiStatusClear());
      setIsUpdatedSuccess(false);
    }
  }, [isSucceed]);

  const formik = useFormik({
    initialValues: jobData,
    validationSchema: addEditJobSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });

  const handleSubmit = (values) => {
    const formData = new FormData();

    // Convert `values` to FormData
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'experience' || key === 'maxExperience') {
        formData.append(key, value !== null && value !== undefined ? value.toString() : '');
      } else if (key === 'jobImage') {
        if (typeof value !== 'string') {
          formData.append(key, value);
        }
      } else if (key === 'skills') {
        for (var i = 0; i < values[key]?.length; i++) {
          formData.append('skills[]', values[key][i]);
        }
      } else if (key === 'technologies') {
        if (Array.isArray(values[key]) && values[key].length > 0) {
          for (var j = 0; j < values[key].length; j++) {
            formData.append('technologies', values[key][j]);
          }
        } else {
          formData.append('technologies', '');
        }
      } else {
        formData.append(key, value);
      }
    });
    if (ID) {
      dispatch(
        editJob({
          payload: formData,
          ID: ID
        })
      );
    } else {
      dispatch(addJob(formData));
    }
    setIsUpdatedSuccess(true);
  };

  const handleSuggestion = (event) => {
    setSkilSuggestion(onSuggestSkils(event));
  };

  return (
    <Dialog
      header="Job"
      className={classNames('card overflow-hidden resize-none notes-dialog')}
      visible={show}
      style={{ width: '850px', height: '800px' }}
      onHide={() => {
        onHide();
        handleReset();
      }}>
      <div className="form-box-wrapper">
        <form onSubmit={formik.handleSubmit} className="p-fluid" autoComplete="off">
          <div className="form-row-wrapper">
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.JOB_TITLE}
                    isMandatory
                    formik={formik}
                    name="jobTitle"
                    placeholder={LABEL_TYPES.JOB_TITLE}
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.JOB_POSITION}
                    isMandatory
                    formik={formik}
                    name="position"
                    placeholder={LABEL_TYPES.JOB_POSITION}
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikInputNumber
                    labelText={LABEL_TYPES.JOB_MIN_EXPERIENCE}
                    isMandatory
                    formik={formik}
                    name="experience"
                    placeholder={LABEL_TYPES.JOB_EXPERIENCE_PLACEHOLDER}
                    onValueChange={(e) => {
                      if (e.value === null || /^\d*\.?\d*$/.test(e.value)) {
                        formik.setFieldValue('experience', e.value);
                      }
                    }}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    max={100}
                    allowEmpty
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikInputNumber
                    labelText={LABEL_TYPES.JOB_MAX_EXPERIENCE}
                    isMandatory
                    formik={formik}
                    name="maxExperience"
                    placeholder={LABEL_TYPES.JOB_EXPERIENCE_PLACEHOLDER}
                    onValueChange={(e) => {
                      if (e.value === null || /^\d*\.?\d*$/.test(e.value)) {
                        formik.setFieldValue('maxExperience', e.value);
                      }
                    }}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    max={100}
                    allowEmpty
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikDropdown
                    labelText={LABEL_TYPES.JOB_MODE}
                    isMandatory
                    formik={formik}
                    name="jobMode"
                    optionLabel="name"
                    optionValue="code"
                    options={JobModes}
                    className={'w-full'}
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group autocomplete">
                  <FormikAutocomplete
                    labelText={LABEL_TYPES.JOB_SKILLS}
                    isMandatory
                    formik={formik}
                    name="skills"
                    placeholder={LABEL_TYPES.SOFT_SKILLS}
                    multiple
                    suggestions={skilSuggestion}
                    completeMethod={handleSuggestion}
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikMultiSelect
                    labelText={LABEL_TYPES.TECHNOLOGY}
                    isMandatory
                    formik={formik}
                    name="technologies"
                    placeholder={LABEL_TYPES.TECHNOLOGY}
                    optionLabel="name"
                    optionValue="value"
                    options={
                      interviewTechnologyType?.length
                        ? interviewTechnologyType.map((tech) => ({
                            name: tech.Technology,
                            value: tech.ID
                          }))
                        : []
                    }
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <div className="form-group-outer upload-document-wrapper">
                <div className="custom-form-group">
                  <Label htmlFor="file" text={BUTTON_TYPES.JOB_LOGO} isBold />
                  <input
                    hidden
                    ref={imageRef}
                    type="file"
                    id="jobImage"
                    name="jobImage"
                    onChange={(e) => {
                      formik.setFieldValue('jobImage', e.target.files[0]);
                      setImageName(e.target.files[0].name);
                      setIsImage('');
                    }}
                    accept="image/png, image/jpeg ,image/jpg"
                    className={classNames({
                      'p-invalid': isFormFieldValid(formik, 'jobImage')
                    })}
                  />
                </div>
                <div className="flex flex-wrap align-items-center">
                  <CustomButton
                    variant="contained"
                    className="gray-btn"
                    onClick={() => {
                      imageRef.current.click();
                    }}>
                    <div>
                      <i className="pi pi-fw pi-upload mr-1" />
                    </div>
                  </CustomButton>
                  {ID ? (
                    isImage ? (
                      <ResumeButton
                        onClick={() => openResumeLink(`${imagePathBase}/${ID}/${isImage}`)}
                      />
                    ) : (
                      <p>{imageName}</p>
                    )
                  ) : (
                    <p>{imageName}</p>
                  )}
                </div>
              </div>
              {getFormErrorMessage(formik, 'jobImage')}
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label for="jobDescription" text={LABEL_TYPES.JOB_DESCRIPTION} isMandatory />
                  <Editor
                    id="jobDescription"
                    name="jobDescription"
                    onInit={(_evt, editor) => (editorRef.current = editor)}
                    value={formik.values.jobDescription}
                    init={{
                      height: 500,
                      menubar: true,
                      branding: false,
                      plugins: TINY_MCE_PLUGINS,
                      ...(isDarkMode() && { content_style: `body { color: #FFF }` })
                    }}
                    onEditorChange={(editor) => formik.setFieldValue('jobDescription', editor)}
                  />
                </div>
                {getFormErrorMessage(formik, 'jobDescription')}
              </div>
            </div>
          </div>
          <div className="form-btn-wrapper justify-flex-end">
            <CustomButton
              variant="contained"
              onClick={() => {
                onHide();
                handleReset();
              }}
              color="error"
              className="gray-btn">
              {BUTTON_TYPES.CANCEL}
            </CustomButton>
            <CustomButton type="submit" variant="contained">
              {BUTTON_TYPES.SAVE}
            </CustomButton>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
